import { useEffect, useMemo, useState } from "react";
import configManager from "../../singleton/ConfigManager";
import configManagerEmitter from "../../utils/configManagerEmitter";
import { resolveRoomResourceLimits } from "../../utils/room-resource-limits-utils";
import {
  ROOM_RESOURCE_STATE_CHANGED,
  getRoomResource,
  isRoomResourceAvailable
} from "../../utils/room-resource-state-utils";

const roomResourceSubscribers = new Set();
let roomResourceStateSnapshot = null;
let subscribedDialog = null;
let subscribedHubChannel = null;
let subscriptionsActive = false;

function currentRoomResourceState() {
  return APP.dialog?.roomResourceState || roomResourceStateSnapshot || null;
}

function notifyRoomResourceSubscribers(snapshot) {
  roomResourceStateSnapshot = snapshot || null;

  for (const subscriber of roomResourceSubscribers) {
    subscriber(roomResourceStateSnapshot);
  }
}

function refreshRoomResourceState() {
  const dialog = APP.dialog;
  dialog?.setRoomResourceLimits?.(
    resolveRoomResourceLimits({
      subscriptionLimits: APP.rtcSfuRoomSession?.limits,
      config: configManager.config,
      roomSettings: APP.hub
    })
  );
  notifyRoomResourceSubscribers(dialog?.roomResourceState || null);
}

function onRoomResourceStateChanged(snapshot) {
  notifyRoomResourceSubscribers(snapshot || subscribedDialog?.roomResourceState || null);
}

function ensureDialogSubscription() {
  const dialog = APP.dialog || null;

  if (subscribedDialog === dialog) {
    return;
  }

  subscribedDialog?.off?.(ROOM_RESOURCE_STATE_CHANGED, onRoomResourceStateChanged);
  subscribedDialog = dialog;
  subscribedDialog?.on?.(ROOM_RESOURCE_STATE_CHANGED, onRoomResourceStateChanged);
}

function ensureHubChannelSubscription() {
  const hubChannel = APP.hubChannel || null;

  if (subscribedHubChannel === hubChannel) {
    return;
  }

  subscribedHubChannel?.removeEventListener?.("permissions_updated", refreshRoomResourceState);
  subscribedHubChannel = hubChannel;
  subscribedHubChannel?.addEventListener?.("permissions_updated", refreshRoomResourceState);
}

function ensureRoomResourceSubscriptions() {
  if (!subscriptionsActive) {
    configManagerEmitter.on("configUpdated", refreshRoomResourceState);
    subscriptionsActive = true;
  }

  ensureDialogSubscription();
  ensureHubChannelSubscription();
  refreshRoomResourceState();
}

function releaseRoomResourceSubscriptions() {
  if (roomResourceSubscribers.size > 0) {
    return;
  }

  if (subscriptionsActive) {
    configManagerEmitter.off("configUpdated", refreshRoomResourceState);
    subscriptionsActive = false;
  }

  subscribedDialog?.off?.(ROOM_RESOURCE_STATE_CHANGED, onRoomResourceStateChanged);
  subscribedDialog = null;

  subscribedHubChannel?.removeEventListener?.("permissions_updated", refreshRoomResourceState);
  subscribedHubChannel = null;
  roomResourceStateSnapshot = null;
}

export function useRoomResourceAvailability(resourceId, { locallyActive = false } = {}) {
  const [roomResourceState, setRoomResourceState] = useState(() => currentRoomResourceState());

  useEffect(() => {
    roomResourceSubscribers.add(setRoomResourceState);
    ensureRoomResourceSubscriptions();
    setRoomResourceState(currentRoomResourceState());

    return () => {
      roomResourceSubscribers.delete(setRoomResourceState);
      releaseRoomResourceSubscriptions();
    };
  }, []);

  const resource = useMemo(() => getRoomResource(roomResourceState, resourceId), [roomResourceState, resourceId]);
  const canAcquire = locallyActive || isRoomResourceAvailable(roomResourceState, resourceId);

  return {
    resource,
    roomResourceState,
    canAcquire,
    exhausted: !canAcquire && !!resource
  };
}
